import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { catalog, ContentType, isFutureDated } from "../api/tmdb";
import { stillUrl } from "../api/images";
import DetailHero from "../components/DetailHero";
import PosterRow from "../components/PosterRow";
import { LoadingScreen, ErrorScreen, EmptyScreen } from "../components/StatusScreens";
import { useProgress, getProgress, formatPosition, clearProgress } from "../storage/progress";
import { formatDate } from "../lib/format";

function EpisodeRow({ episode, showTitle, showLanguage }) {
  const navigate = useNavigate();
  const progress = useProgress();
  const saved = getProgress(ContentType.EPISODE, episode.showTmdbId, episode.seasonNumber, episode.episodeNumber);
  const watched = saved?.completed || (saved && saved.positionSeconds / (saved.durationSeconds || 1) >= 0.95);
  const resume = saved && !watched && saved.positionSeconds >= 5;

  const play = () => {
    const params = new URLSearchParams({
      type: "episode",
      id: String(episode.showTmdbId),
      title: showTitle,
      subtitle: `S${episode.seasonNumber} E${episode.episodeNumber}`,
      season: String(episode.seasonNumber),
      episode: String(episode.episodeNumber),
      poster: episode.stillPath || "",
      backdrop: "",
      // The show's original language, so the player starts the episode on the
      // series' own audio track out of a multi-language master.
      lang: showLanguage || "",
      resume: String(saved?.positionSeconds || 0),
    });
    navigate(`/watch?${params.toString()}`);
  };

  return (
    <div className="episode-row">
      <button className="episode-still" onClick={play} aria-label={`Play ${episode.title}`}>
        {episode.stillPath ? (
          <img src={stillUrl(episode.stillPath)} alt="" loading="lazy" />
        ) : (
          <div className="episode-still-fallback"><span>▶</span></div>
        )}
        {watched && <span className="episode-badge">WATCHED</span>}
        {resume && <span className="episode-badge resume">RESUME {formatPosition(saved.positionSeconds)}</span>}
      </button>
      <div className="episode-info">
        <div className="episode-title-row">
          <h3 className="episode-title">{episode.episodeNumber}. {episode.title}</h3>
          {episode.runtimeMinutes && <span className="episode-runtime">{episode.runtimeMinutes}m</span>}
        </div>
        {episode.overview && <p className="episode-overview">{episode.overview}</p>}
        <p className="episode-meta">
          {formatDate(episode.airDate) || `Season ${episode.seasonNumber}`}
          {watched && saved && !saved.completed && (
            <button
              className="link-button"
              onClick={() => clearProgress(ContentType.EPISODE, episode.showTmdbId, episode.seasonNumber, episode.episodeNumber)}
            >
              Mark unwatched
            </button>
          )}
        </p>
      </div>
    </div>
  );
}

export default function TvDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, show: null, error: null });
  const [seasonNumber, setSeasonNumber] = useState(null);
  const [season, setSeason] = useState(null);
  const [seasonLoading, setSeasonLoading] = useState(false);

  const load = useCallback(() => {
    setState({ loading: true, show: null, error: null });
    setSeasonNumber(null);
    setSeason(null);
    catalog
      .tvDetails(Number(id))
      .then((show) => {
        setState({ loading: false, show });
        if (show.seasons.length > 0) setSeasonNumber(show.seasons[0].seasonNumber);
      })
      .catch((error) => setState({ loading: false, show: null, error: error.message || "Something went wrong. Try again." }));
  }, [id]);

  useEffect(load, [load]);

  useEffect(() => {
    if (seasonNumber == null || !state.show) {
      setSeason(null);
      return;
    }
    setSeasonLoading(true);
    let cancelled = false;
    catalog
      .seasonDetails(state.show.tmdbId, state.show.title, seasonNumber)
      .then((s) => {
        if (!cancelled) {
          setSeason(s);
          setSeasonLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setSeasonLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [seasonNumber, state.show]);

  if (state.loading) return <LoadingScreen label="Loading…" />;
  if (state.error) return <ErrorScreen message={state.error} onRetry={load} />;
  const show = state.show;

  // A show that hasn't premiered yet has no episodes to watch.
  if (isFutureDated(show.firstAirDate)) {
    return (
      <EmptyScreen
        title={show.title}
        message={`Not released yet${show.firstAirDate ? ` — premieres ${show.firstAirDate}` : ""}. It will appear here once it's out.`}
      />);
  }

  return (
    <main className="page">
      <DetailHero
        media={{ ...show, contentType: ContentType.TV_SHOW }}
        extraMeta={show.firstAirDate ? <p className="hero-meta muted">{formatDate(show.firstAirDate)}</p> : null}
      />
      <div className="page-content">
        {show.seasons.length > 0 && (
          <section className="seasons-section">
            <div className="season-chips" role="group" aria-label="Season">
              {show.seasons.map((s) => (
                <button
                  key={s.seasonNumber}
                  className={`chip${seasonNumber === s.seasonNumber ? " active" : ""}`}
                  onClick={() => setSeasonNumber(s.seasonNumber)}
                >
                  Season {s.seasonNumber}
                </button>
              ))}
            </div>
            {seasonLoading ? (
              <LoadingScreen label="Loading episodes…" />
            ) : season && season.episodes.length > 0 ? (
              <div className="episode-list">
                {season.episodes.map((episode) => (
                  <EpisodeRow
                    key={episode.tmdbId}
                    episode={episode}
                    showTitle={show.title}
                    showLanguage={show.originalLanguage}
                  />
                ))}
              </div>
            ) : (
              <EmptyScreen title="No episodes" message="Episodes for this season are not available yet." />
            )}
          </section>
        )}
        {show.recommendations.length > 0 && (
          <PosterRow
            title="More Like This"
            items={show.recommendations}
            getProgress={(card) => getProgress(card.contentType, card.tmdbId)}
          />
        )}
      </div>
    </main>
  );
}